/**
 * src/components/layout/TopBannerStack.jsx
 *
 * Wraps every app-shell top banner (BETA notice, environment, save-conflict,
 * write-error, stale-state, api-error, access-approval, payment-warning) in one measured
 * container. Full-screen position:fixed overlays (DocumentEditor,
 * DocumentSlidePanel) read the --top-banner-stack-height custom property to
 * offset their top edge below whatever banners are actually showing right
 * now, instead of only the dev/test environment banner — so a banner that
 * appears while one of those overlays is already open (e.g. a failed
 * autosave while editing) stays visible instead of being painted over.
 *
 * pointer-events:none on the wrapper means an empty margin inside the stack
 * (e.g. while banners are transitioning in/out) can never intercept a click
 * meant for whatever is rendered beneath it. Each banner restores
 * pointer-events:auto on its own rendered box so its dismiss button/links
 * keep working.
 */
import { useLayoutEffect, useRef } from 'react';

export default function TopBannerStack({ children }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const setHeight = () => {
      document.documentElement.style.setProperty(
        '--top-banner-stack-height',
        `${el.offsetHeight}px`
      );
    };
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--top-banner-stack-height', '0px');
    };
  }, []);

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', pointerEvents: 'none' }}>
      {children}
    </div>
  );
}
